// Rhythm-recognition flashcard mini-game.
// Pure client-side: pick N random rhythms from the catalog, show each as a strip with no
// label, learner picks from 5 options, score at end with per-question breakdown.
// Reuses the same waveform engine as the simulator monitor — deterministic generation.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Rhythm } from '../lib/waveforms';
import WaveformLane from '../components/Monitor/WaveformLane';

interface Card {
  rhythm: Rhythm;
  hr: number;
  label: string;
  hint: string;     // shown after answer
}

const CARDS: Card[] = [
  { rhythm: 'sinus',           hr: 75,  label: 'Sinus rhythm',                           hint: 'Regular, narrow QRS, P wave precedes each QRS, PR 0.12-0.20s.' },
  { rhythm: 'sinus_tach',      hr: 125, label: 'Sinus tachycardia',                       hint: 'Same morphology as sinus but >100/min. P-T fusion at high rates.' },
  { rhythm: 'brady',           hr: 45,  label: 'Sinus bradycardia',                       hint: 'Sinus rhythm <60/min. Causes: athletes, drugs (β-blocker), MI (inferior), raised ICP.' },
  { rhythm: 'af',              hr: 110, label: 'Atrial fibrillation',                     hint: 'Irregularly irregular, NO P waves, fibrillatory baseline. Most common arrhythmia.' },
  { rhythm: 'aflutter',        hr: 150, label: 'Atrial flutter with 2:1 conduction',      hint: 'Sawtooth F waves at 300/min, AV conduction ratio sets ventricular rate (2:1 → 150).' },
  { rhythm: 'svt',             hr: 175, label: 'Supraventricular tachycardia (SVT)',      hint: 'Regular narrow-complex tachy 150-220/min, no clear P waves. Try vagal manoeuvres → adenosine.' },
  { rhythm: 'vt_pulse',        hr: 165, label: 'Monomorphic ventricular tachycardia',     hint: 'Broad regular QRS (>0.12s), no P waves visible, T discordant. If unstable → sync cardiovert.' },
  { rhythm: 'vf',              hr: 0,   label: 'Ventricular fibrillation (VF)',           hint: 'Chaotic irregular waveform, no QRS, no pulse — SHOCKABLE. Spectral peak 4-10 Hz.' },
  { rhythm: 'torsades',        hr: 0,   label: 'Torsades de pointes',                     hint: 'Polymorphic VT with axis twisting around baseline. Treatment: magnesium 2g IV.' },
  { rhythm: 'asystole',        hr: 0,   label: 'Asystole',                                hint: 'Flat line with mild baseline wander. Non-shockable. Treatment: CPR + adrenaline + reversible causes.' },
  { rhythm: 'p_wave_asystole', hr: 0,   label: 'P-wave asystole (ventricular standstill)', hint: 'P waves continue but no QRS. Pacing may rarely salvage; otherwise treat as asystole.' },
  { rhythm: 'av1',             hr: 68,  label: 'First-degree AV block',                   hint: 'PR interval >0.20s but every P → QRS. Usually benign but watch for progression.' },
  { rhythm: 'chb',             hr: 32,  label: 'Complete (3rd-degree) AV block',          hint: 'P waves and QRS dissociated. Atropine often ineffective — pacing is definitive.' },
  { rhythm: 'idioventricular', hr: 30,  label: 'Idioventricular rhythm',                  hint: 'Slow, broad ventricular escape. Post-ROSC (after reperfusion) or terminal.' },
  { rhythm: 'agonal',          hr: 0,   label: 'Agonal rhythm',                           hint: 'Slow, irregular, wide complexes that progressively slow and broaden before asystole.' },
  { rhythm: 'pea_broad',       hr: 40,  label: 'PEA with broad complexes',                hint: 'Organised electrical activity but NO pulse. Non-shockable. Hunt the 4Hs/4Ts.' },
];

const OPTIONS_PER_CARD = 5;

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deterministic-per-session RNG so options shuffle predictably for a given seed
function makeRng(seed: number) {
  let s = seed;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

interface QuizCard {
  card: Card;
  options: Card[];     // 5 cards: 1 correct + 4 distractors
  startedAt: number;
}

export default function RhythmFlashcards() {
  const [quiz, setQuiz] = useState<QuizCard[]>([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Array<{ chosen: Card; correct: boolean; time_ms: number }>>([]);
  const [revealed, setRevealed] = useState(false);
  const [tick, setTick] = useState(0);
  const tStart = useRef<number>(Date.now());

  // Build a 10-card quiz on mount (or "play again")
  function newQuiz() {
    const rng = makeRng(Date.now() & 0xffff);
    const cards = shuffle(CARDS, rng).slice(0, 10);
    const q = cards.map((c) => {
      const distractors = shuffle(CARDS.filter((x) => x.rhythm !== c.rhythm), rng).slice(0, OPTIONS_PER_CARD - 1);
      const options = shuffle([c, ...distractors], rng);
      return { card: c, options, startedAt: Date.now() };
    });
    setQuiz(q);
    setIdx(0);
    setAnswers([]);
    setRevealed(false);
    tStart.current = Date.now();
  }

  useEffect(() => { newQuiz(); }, []);

  // Animate the rendered strip by advancing a virtual time
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 80);
    return () => clearInterval(id);
  }, []);

  const current = quiz[idx];
  const sim_t_ms = useMemo(() => Date.now() - tStart.current, [tick]); // updates ~12.5/s

  function answer(choice: Card) {
    if (!current || revealed) return;
    const correct = choice.rhythm === current.card.rhythm;
    const time_ms = Date.now() - current.startedAt;
    setAnswers((a) => [...a, { chosen: choice, correct, time_ms }]);
    setRevealed(true);
  }

  function next() {
    if (idx + 1 >= quiz.length) return; // end-of-quiz handled below
    setIdx(idx + 1);
    setRevealed(false);
    if (quiz[idx + 1]) quiz[idx + 1].startedAt = Date.now();
  }

  if (quiz.length === 0) return <div className="max-w-3xl mx-auto px-4 py-10 text-mono-white/60">Loading…</div>;

  const done = answers.length === quiz.length;
  const correctCount = answers.filter((a) => a.correct).length;
  const avgTime = answers.length > 0 ? answers.reduce((s, a) => s + a.time_ms, 0) / answers.length / 1000 : 0;

  // Result screen
  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/dashboard" className="text-mono-white/50 hover:text-mono-white text-sm">← Back to dashboard</Link>
        <h1 className="text-2xl font-semibold mt-3 mb-2">Rhythm flashcards — results</h1>
        <div className="panel p-6 mb-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-mono-white/40">Score</div>
              <div className="text-4xl font-mono tabular-nums text-mono-green">{correctCount}<span className="text-base text-mono-white/40 ml-1">/ {quiz.length}</span></div>
              <div className="text-sm text-mono-white/60 mt-1">Avg time per rhythm: {avgTime.toFixed(1)} s</div>
            </div>
            <button onClick={newQuiz} className="btn-primary">Play again with fresh rhythms</button>
          </div>
        </div>
        <h2 className="text-mono-white/60 uppercase tracking-widest text-xs mb-3">Breakdown</h2>
        <div className="space-y-3">
          {quiz.map((q, i) => {
            const a = answers[i];
            return (
              <div key={i} className={`panel p-4 ${a.correct ? '' : 'border-l-4 border-alarm-red'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{i + 1}. {q.card.label}</span>
                  <span className={a.correct ? 'text-mono-green text-xs font-semibold' : 'text-alarm-red text-xs font-semibold'}>
                    {a.correct ? '✓ Correct' : `✖ You said: ${a.chosen.label}`}
                  </span>
                </div>
                <p className="text-sm text-mono-white/80">{q.card.hint}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Quiz screen
  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <Link to="/dashboard" className="text-mono-white/50 hover:text-mono-white text-sm">← Back to dashboard</Link>
      <div className="flex justify-between items-center mt-3 mb-4">
        <h1 className="text-2xl font-semibold">Rhythm flashcards</h1>
        <span className="text-mono-white/60 text-sm">{idx + 1} / {quiz.length} · score {answers.filter((a) => a.correct).length}/{answers.length}</span>
      </div>
      <p className="text-mono-white/60 text-sm mb-6">Identify the rhythm. The strip animates live — same generator as the scenario monitor.</p>

      <div className="panel p-3 mb-6 bg-ink-950">
        <WaveformLane
          lane="ecg"
          vitals={{ rhythm: current.card.rhythm, hr: current.card.hr, spo2: 98, etco2_mmhg: 38, rr: 14 }}
          sim_t_ms={sim_t_ms}
          cpr_active={false}
          cpr_quality_pct={0}
          cpr_rate_cpm={0}
          intubated={false}
          height={140}
        />
        <div className="text-[10px] text-mono-white/30 mt-1 font-mono tabular-nums tracking-widest text-right">
          HR {current.card.hr > 0 ? current.card.hr : '--'} bpm · 25 mm/s
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {current.options.map((opt) => {
          const isCorrect = revealed && opt.rhythm === current.card.rhythm;
          const isWrongPick = revealed && answers[idx] && answers[idx].chosen.rhythm === opt.rhythm && !isCorrect;
          let cls = 'bg-ink-800 hover:bg-ink-950 text-mono-white/85';
          if (isCorrect) cls = 'bg-mono-green/15 text-mono-green ring-1 ring-mono-green/40';
          else if (isWrongPick) cls = 'bg-alarm-red/15 text-alarm-red ring-1 ring-alarm-red/40';
          return (
            <button
              key={opt.rhythm}
              onClick={() => answer(opt)}
              disabled={revealed}
              className={`px-4 py-3 rounded text-left text-sm transition-colors ${cls} disabled:cursor-default`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {revealed && (
        <div className="mt-6 panel p-4 bg-ink-800/50">
          <div className="text-xs uppercase tracking-widest text-mono-white/40 mb-1">Teaching point</div>
          <p className="text-sm text-mono-white/85">{current.card.hint}</p>
          <div className="mt-3 flex justify-end">
            <button onClick={next} className="btn-primary text-sm py-2 px-4">
              {idx + 1 >= quiz.length ? 'See results' : 'Next →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
